// ============================================================
// web/src/features/apprentissage/SurveillanceCarte.jsx — CE QUE LE
// PROGRAMME MESURE DE LUI-MÊME (étape 4 du plan du 15 septembre 2026).
// ------------------------------------------------------------
// Trois blocs, tous en lecture :
//
//  - la COURBE : chaque nuit, les pièces nouvellement enregistrées sont
//    re-chiffrées sans leur propre mémoire et comparées à ce que le
//    deviseur a retenu. L'écart médian doit baisser semaine après semaine ;
//  - les MARGES D'ATELIER, calculées sur les retours d'atelier par des
//    règles fixées d'avance, et les retours écartés avec leur raison ;
//  - le RAPPORT de l'analyste de la semaine — de l'information, jamais un
//    chiffre qu'il aurait modifié.
// ============================================================
import { useCallback } from 'react';
import { Card, CardHead, CardBody } from '../../ui/Card.jsx';
import Table from '../../ui/Table.jsx';
import Badge from '../../ui/Badge.jsx';
import { EmptyState, ErrorState, SkeletonRows } from '../../ui/States.jsx';
import { useResource } from '../../lib/useResource.js';
import { api } from '../../lib/api.js';
import { formatDate, formatNumber } from '../../lib/format.js';
import HabitudesTable from './HabitudesTable.jsx';
import { BLOCS_GAMME, LIBELLE_CATEGORIE } from '@shared/gamme.js';

const LIBELLE_GROUPE = (cle) => {
  const [machine, bloc] = cle.split('|');
  const m = { '*': 'toutes machines', centre_5axes: 'centre 5 axes', centre_3axes: 'centre 3 axes', tour_cn: 'tour CN' }[machine] ?? machine;
  // Par BLOC depuis le 28 septembre 2026, avec les mots de `shared/`. Une
  // ancienne clé par catégorie reste lisible jusqu'au premier recalcul.
  const b = BLOCS_GAMME.find((x) => x.cle === bloc);
  const c = b ? b.label.toLowerCase() : `${(LIBELLE_CATEGORIE[bloc] ?? bloc).toLowerCase()} (ancien calcul)`;
  return `${m} — ${c}`;
};

const ecart = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${formatNumber(Math.round(v))} %`);

export function SurveillanceCarte() {
  const charger = useCallback(() => api.get('/apprentissage/surveillance'), []);
  const { data, loading, error, reload } = useResource(charger);

  if (loading) return <SkeletonRows rows={3} />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const rapport = data.rapport?.contenu ?? null;
  const marges = Object.entries(data.marges?.valeurs ?? {});

  return (
    <>
      <Card>
        <CardHead title="Le programme se re-chiffre chaque nuit" />
        <CardBody className="stack">
          <p className="field__hint">
            Chaque pièce que vous enregistrez est re-chiffrée la nuit suivante, <strong>sans
            sa propre mémoire</strong>, puis comparée à ce que vous avez retenu. Si l’écart médian ne
            baisse pas au fil des semaines, le programme n’apprend pas — et cet écran le dit.
          </p>
          {data.courbe.length ? (
            <Table
              caption="Écart médian sur le prix, par semaine"
              columns={[
                { key: 'semaine', label: 'Semaine du', render: (r) => formatDate(r.semaine) },
                { key: 'pieces', label: 'Pièces' },
                { key: 'ecart_median', label: 'Écart médian', render: (r) => `${formatNumber(Math.round(r.ecart_median))} %` },
                { key: 'dans_15', label: 'À ±15 %', render: (r) => `${r.dans_15}/${r.pieces}` },
              ]}
              rows={data.courbe}
              getRowKey={(r) => r.semaine}
            />
          ) : (
            <EmptyState title="Aucune mesure de nuit pour l’instant">
              La première apparaîtra la nuit qui suit l’enregistrement d’une pièce corrigée.
            </EmptyState>
          )}
          {data.mesures.length ? (
            <Table
              caption="Dernières pièces re-chiffrées"
              columns={[
                { key: 'piece', label: 'Pièce', render: (r) => r.ref ?? r.name ?? r.piece_id },
                { key: 'created_at', label: 'Mesurée le', render: (r) => formatDate(r.created_at) },
                { key: 'ecart_prix', label: 'Prix', render: (r) => (r.erreur ? <Badge tone="danger">échec</Badge> : ecart(r.ecart_prix)) },
                { key: 'ecart_mise_en_route', label: 'Mise en route', render: (r) => ecart(r.ecart_mise_en_route) },
                { key: 'ecart_production', label: 'Production', render: (r) => ecart(r.ecart_production) },
                { key: 'prises_justes', label: 'Prises', render: (r) => (r.prises_justes == null ? '—' : r.prises_justes ? 'justes' : 'fausses') },
                { key: 'taille_memoire', label: 'Pièces en mémoire' },
              ]}
              rows={data.mesures.slice(0, 20)}
              getRowKey={(r) => r.id}
            />
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardHead title="Habitudes de l’atelier — apprises de vos consignes" />
        <CardBody className="stack">
          <p className="field__hint">
            Chaque consigne est découpée en façons de travailler, sans les cotes de la pièce. Une
            habitude s’active quand <strong>au moins 3 pièces</strong> la suivent, que <strong>70 %</strong> des
            pièces concernées la suivent et que <strong>moins de 20 %</strong> font autrement ; elle
            s’éteint seule sinon. Seules les habitudes actives, et utiles à la pièce, partent au chiffrage.
          </p>
          {(data.habitudes ?? []).length ? (
            <HabitudesTable habitudes={data.habitudes} onChange={reload} />
          ) : (
            <EmptyState title="Aucune consigne découpée pour l’instant">
              La première passe de nuit découpera les consignes des pièces enregistrées.
            </EmptyState>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHead title="Marges d’atelier — ce que les retours changent à la mémoire" />
        <CardBody className="stack">
          <p className="field__hint">
            Une pièce passée sert de référence à <strong>temps réel × marge d’atelier</strong>. La
            marge est la médiane de « retenu ÷ réel » sur au moins 3 pièces, par bloc (le contrôle
            du premier article compte en mise en route, celui de la série en production), bornée
            entre 1,0 et 2,5, et elle ne bouge pas de plus de 10 % par semaine. Le plancher à 1,0
            est une règle : une référence ne descend jamais sous le réel mesuré. Rien n’est à
            valider ; aucun devis remis n’est modifié.
          </p>
          {marges.length ? (
            <Table
              caption="Marges en vigueur"
              columns={[
                { key: 'cle', label: 'Groupe', render: (r) => LIBELLE_GROUPE(r.cle) },
                { key: 'marge', label: 'Marge', render: (r) => `× ${formatNumber(r.marge)}` },
                { key: 'pieces', label: 'Fondée sur', render: (r) => `${r.pieces.length} pièces` },
              ]}
              rows={marges.map(([cle, v]) => ({ cle, ...v }))}
              getRowKey={(r) => r.cle}
            />
          ) : null}
          {marges.length ? (
            <p className="field__hint">
              <strong>La mise en route de référence est le temps réellement passé, pas le temps
              devisé.</strong> Sa marge reste à 1,0 tant que moins de 10 pièces sont revenues de
              l’atelier : aucune correction de niveau avant.
            </p>
          ) : (
            <EmptyState title="Pas encore assez de retours d’atelier">
              Une marge s’établit à partir de 3 pièces revenues de l’atelier avec leurs temps réels.
              D’ici là, les pièces servent de référence à leur temps retenu.
            </EmptyState>
          )}
          {data.marges.ecartees.length ? (
            <Table
              caption="Retours d’atelier écartés, et pourquoi"
              columns={[
                { key: 'piece_id', label: 'Pièce' },
                { key: 'raison', label: 'Raison' },
              ]}
              rows={data.marges.ecartees}
              getRowKey={(r) => r.piece_id}
            />
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardHead title="Le rapport de la semaine" />
        <CardBody className="stack">
          {!data.rapport ? (
            <EmptyState title="Aucun rapport pour l’instant">
              L’analyste écrit son premier rapport une semaine après la mise en service.
            </EmptyState>
          ) : data.rapport.erreur ? (
            <ErrorState error={new Error(data.rapport.erreur)} onRetry={reload} />
          ) : (
            <>
              <p className="field__hint">
                Du {formatDate(data.rapport.du)} au {formatDate(data.rapport.au)} — rédigé par un agent, qui
                ne modifie aucun chiffre.
              </p>
              <p>{rapport.resume}</p>
              {(rapport.constats ?? []).map((c) => (
                <div key={c.titre}>
                  <strong>{c.titre}</strong>
                  <p>{c.detail}</p>
                </div>
              ))}
              {(rapport.regles_possibles ?? []).length ? (
                <Table
                  caption="Consignes qui reviennent — des règles possibles"
                  columns={[
                    { key: 'phrase', label: 'Phrase' },
                    { key: 'occurrences', label: 'Fois' },
                  ]}
                  rows={rapport.regles_possibles}
                  getRowKey={(r) => r.phrase}
                />
              ) : null}
            </>
          )}
        </CardBody>
      </Card>
    </>
  );
}

export default SurveillanceCarte;
